import { ArrowBack, ExpandLess, ExpandMore, Save, Schedule, Timelapse, WarningAmber } from '@mui/icons-material';
import { Alert, Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid2, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, Controller, FormProvider, useFieldArray, useFormState, useWatch } from 'react-hook-form';
import { SchoolShiftFormInput } from 'services/SchoolShift/schemas';
import { useSchoolShift } from '../hooks/useSchoolShift';
import SchoolShiftDayRow from './SchoolShiftDayRow';

interface Props {
  state: ReturnType<typeof useSchoolShift>['state'];
  actions: ReturnType<typeof useSchoolShift>['actions'];
  form: ReturnType<typeof useSchoolShift>['form'];
}

const ConfigurationTipsAlert = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const directionByNameParts = t('schoolShiftPage.tips.directionByNameBody').split(t('schoolShiftPage.tips.deviceListLink'));

  return (
    <Alert 
      severity="info" 
      sx={{ 
        mb: 3, 
        borderRadius: 2,
        transition: 'all 0.3s ease-in-out',
        '& .MuiAlert-message': { width: '100%' } 
      }}
    >
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        onClick={() => setOpen(!open)}
        sx={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <Typography variant="subtitle2" fontWeight="bold" color="info.dark">
          {t('schoolShiftPage.tipsTitle')}
        </Typography>
        {open ? <ExpandLess color="info" /> : <ExpandMore color="info" />}
      </Stack>
      
      <Collapse in={open}>
        <Box sx={{ mt: 2 }}>
          <Stack spacing={1.5} component="ul" sx={{ m: 0, pl: 2, typography: 'body2', color: 'text.secondary' }}>
            <li>
              <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
                {t('schoolShiftPage.tips.directionByNameTitle')}
              </Typography>{' '}
              {directionByNameParts[0]}
              <Typography component="a" href="devices/facialControl" variant="body2" sx={{ fontWeight: 'bold', color: 'info.dark', textDecoration: 'underline' }}>
                {t('schoolShiftPage.tips.deviceListLink')}
              </Typography>
              {directionByNameParts[1] || ''}
            </li>

            <li>
              <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
                {t('schoolShiftPage.tips.directionMatchTitle')}
              </Typography>{' '}
              {t('schoolShiftPage.tips.directionMatchBody')}
            </li>

            <li>
              <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
                {t('schoolShiftPage.tips.bidirectionalTitle')}
              </Typography>{' '}
              {t('schoolShiftPage.tips.bidirectionalBody')}
            </li>

            <li>
              <Typography component="span" variant="body2" fontWeight="bold" color="text.primary">
                {t('schoolShiftPage.tips.midnightTitle')}
              </Typography>{' '}
              {t('schoolShiftPage.tips.midnightBody')}
            </li>
          </Stack>
        </Box>
      </Collapse>
    </Alert>
  );
};

const WeeklyScheduleErrorAlert = ({ control }: { control: Control<SchoolShiftFormInput> }) => {
  const { errors } = useFormState({ control }); 
  
  const errorObj = errors.weeklySchedule;
  const errorMessage = errorObj?.root?.message || errorObj?.message;

  if (!errorMessage) return null;

  return (
    <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
      {errorMessage as string}
    </Alert>
  );
};

const ShiftTitle = ({ control, actions }: { control: Control<SchoolShiftFormInput>, actions: Props['actions'] }) => {
  const { t } = useTranslation();
  const currentName = useWatch({
    control,
    name: 'name',
    defaultValue: t('schoolShiftPage.newShiftDefaultName')
  }) || t('schoolShiftPage.newShiftDefaultName');

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
      <IconButton 
        sx={{ display: { xs: 'flex', md: 'none' }, ml: -1 }} 
        onClick={() => actions.handleSelectShift(0)} 
        size="small"
      >
        <ArrowBack />
      </IconButton>
      <Typography variant="h4" fontWeight="bold" noWrap title={currentName} sx={{ flex: 1, minWidth: 0 }}>
        {currentName}
      </Typography>
    </Stack>
  );
};

export default function SchoolShiftEditor({ state, actions, form }: Props) {
  const { selectedId, isFetchingDetail, isCreating, isUpdating, devices } = state;
  const { t } = useTranslation();
  const { onSave } = actions;
  const { control, handleSubmit } = form;
  const { fields: dayFields } = useFieldArray({ control, name: "weeklySchedule" });
  const [clearModalOpen, setClearModalOpen] = useState(false);

  if (!selectedId) {
    return (
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flex: 1, flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 4, textAlign: 'center', bgcolor: 'background.default', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Box sx={{ p: 4, borderRadius: '50%', bgcolor: 'background.paper', mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <Timelapse sx={{ fontSize: 60, opacity: 0.2 }} />
        </Box>
        <Typography variant="h5" fontWeight="bold">{t('schoolShiftPage.selectedNoneTitle')}</Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 400, mt: 1 }}>{t('schoolShiftPage.selectedNoneDescription')}</Typography>
      </Box>
    );
  }

  const handleClearAllSchedules = () => {
    const currentValues = form.getValues();
    form.reset({
      ...currentValues,
      weeklySchedule: currentValues.weeklySchedule.map(day => ({
        ...day,
        active: false,
        timeSlots: []
      }))
    });
    setClearModalOpen(false);
  };

  return (
    <Box 
      sx={{ 
        flex: 1, 
        display: { xs: selectedId ? 'flex' : 'none', md: 'flex' }, 
        flexDirection: 'column', 
        overflow: 'hidden', 
        border: '1px solid', 
        borderColor: 'divider', 
        borderRadius: 2, 
        bgcolor: 'background.default' 
      }}
    >
      <FormProvider {...form}>
        <Box component="form" onSubmit={handleSubmit(onSave)} sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: { xs: 2, md: 4 }, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper', position: 'relative' }}>
            
            {isFetchingDetail && selectedId !== 'new' && (
               <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(0, 0, 0, 0.6)', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <CircularProgress />
               </Box>
            )}

            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 3, gap: 2 }}>
              <ShiftTitle control={control} actions={actions} />
              <Button 
                type="submit" 
                variant="contained" 
                startIcon={<Save />} 
                disableElevation 
                disabled={isCreating || isUpdating}
                sx={{ flexShrink: 0 }}
              >
                {isCreating || isUpdating ? 'Salvando...' : (selectedId === 'new' ? t('schoolShiftPage.saveNew') : t('schoolShiftPage.saveEdit'))}
              </Button>
            </Stack>

            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                  <Controller name="name" control={control} render={({ field, fieldState }) => (
                  <TextField {...field} fullWidth label={t('schoolShiftPage.nameLabel')} size="small" error={!!fieldState.error} helperText={fieldState.error?.message} slotProps={{ htmlInput: { maxLength: 40 } }}/>
                  )} />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <Controller name="deviceIds" control={control} render={({ field, fieldState }) => (
                <TextField {...field} select slotProps={{ select: { multiple: true } }} fullWidth label={t('schoolShiftPage.devicesLabel')} size="small" error={!!fieldState.error} helperText={fieldState.error?.message}>
                    {devices.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
                </TextField>
                )} />
                </Grid2>
            </Grid2>
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', p: { xs: 2, md: 4 } }}>
            <Box sx={{ width: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Schedule color="primary" />
                    <Typography variant="h6" fontWeight="bold">{t('schoolShiftPage.weeklyScheduleTitle')}</Typography>
                </Stack>
                
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={() => setClearModalOpen(true)}
                  sx={{ 
                    color: 'gray', 
                    borderColor: 'divider',
                    textTransform: 'none'
                  }}
                >
                  {t('schoolShiftPage.clearSchedules')}
                </Button>
              </Stack>
    
              <ConfigurationTipsAlert />

              <WeeklyScheduleErrorAlert control={control} />

              <Divider sx={{ mb: 3 }} />
              <Stack spacing={2}>
                {dayFields.map((day, idx) => (
                  <SchoolShiftDayRow key={day.id} index={idx} />
                ))}
              </Stack>
            </Box>
          </Box>
        </Box>
      </FormProvider>

      <Dialog 
        open={clearModalOpen} 
        onClose={() => setClearModalOpen(false)}
        slotProps={{
          paper: {
            sx: { 
              borderRadius: 2, 
              bgcolor: 'background.paper', 
              backgroundImage: 'none',
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 400
            }
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1.5, pb: 1 }}>
          <WarningAmber color="error" /> {t('schoolShiftPage.confirmClearTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {t('schoolShiftPage.confirmClearQuestion')}
          </Typography>
          <Typography color="text.secondary">
            {t('schoolShiftPage.confirmClearDescription')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button 
            onClick={() => setClearModalOpen(false)} 
            color="inherit" 
            sx={{ textTransform: 'none' }}
          >
            {t('schoolShiftPage.cancel')}
          </Button>
          <Button 
            onClick={handleClearAllSchedules}
            variant="contained" 
            color="error" 
            disableElevation
            sx={{ textTransform: 'none', fontWeight: 'bold' }}
          >
            {t('schoolShiftPage.clearAll')}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}